import { Empty, Skeleton, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { globalMapStatusStyle } from "../helpers/status-style"
import { EntityIcon } from "../map/EntityIcon.tsx"
import type { EntityType, MapFeature } from "../redux/global-map.types"
import { nearbyBreakdownRows } from "./nearby-breakdown-rows.tsx"
import {
  NEARBY_RADIUS_MI,
  type UseNearbyFeaturesResult
} from "./useNearbyFeatures.ts"

const { Text } = Typography

interface QuickViewNearbyProps {
  status: UseNearbyFeaturesResult["status"]
  rows: MapFeature[]
  counts: UseNearbyFeaturesResult["counts"]
  onSelectFeature: (feature: {
    id: string
    entityType: EntityType
    latitude: number
    longitude: number
  }) => void
}

/**
 * "Nearby" — the same resolved-point radius filter as "Look Around a Zip
 * Code" (`GlobalMapQuery._apply_near_filter`), anchored at THIS record's own
 * coordinate instead of a geocoded zip. `status`/`rows`/`counts` all come
 * from `EntityQuickView`'s own single `useNearbyFeatures` call (shared with
 * the always-visible `NearbyBreakdownSummary` under Overview/Files) rather
 * than fetched here.
 *
 * The row list renders FIRST, with the records breakdown (counts) below it
 * — per explicit product direction, reversing the original top-to-bottom
 * order. The Lead/Project filter toggle that used to sit between them is
 * removed entirely (also per explicit direction): every fetched row shows,
 * unfiltered.
 *
 * Selecting a row is handed to the caller (`onSelectFeature`) rather than
 * dispatched here directly — `MapTab.tsx`'s own `handleSelectNearbyFeature`
 * both swaps the quick view AND recenters the camera on the clicked
 * record's coordinate (already on hand from the shared fetch), and
 * separately maintains the "Back" stack that returns to whichever record's
 * own Nearby list this selection was made from.
 */
function QuickViewNearby({
  status,
  rows,
  counts,
  onSelectFeature
}: QuickViewNearbyProps) {
  const { t } = useTranslation()

  const breakdownRows = nearbyBreakdownRows(t)
  const totalCount = counts
    ? counts.project + counts.lead + counts.subcontractor + counts.appointment
    : null

  return (
    <div className="flex flex-col gap-2" data-testid="quick-view-nearby">
      {status === "loading" && (
        <Skeleton active title={false} paragraph={{ rows: 3 }} />
      )}

      {status === "no-location" && (
        <Text type="secondary" className="text-xs">
          {t("globalMap.quickView.nearby.noLocation")}
        </Text>
      )}

      {status === "error" && (
        <Text type="secondary" className="text-xs">
          {t("globalMap.quickView.nearby.loadError")}
        </Text>
      )}

      {status === "ready" && rows.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("globalMap.quickView.nearby.empty", {
            miles: NEARBY_RADIUS_MI
          })}
        />
      )}

      {status === "ready" && rows.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {rows.map((feature) => (
            <li key={feature.id}>
              <button
                type="button"
                onClick={() =>
                  onSelectFeature({
                    id: feature.id,
                    entityType: feature.entity_type,
                    latitude: feature.latitude,
                    longitude: feature.longitude
                  })
                }
                className="flex w-full min-w-0 flex-col gap-0.5 rounded px-1 py-1 text-left hover:bg-gray-50"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <EntityIcon
                    entityType={feature.entity_type}
                    className="size-6"
                  />
                  <Text
                    ellipsis={{ tooltip: feature.title }}
                    className="min-w-0 flex-1 text-sm"
                  >
                    {feature.title}
                  </Text>
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
                    style={globalMapStatusStyle(
                      feature.entity_type,
                      feature.status_label
                    )}
                  >
                    {feature.status_label}
                  </span>
                </span>
                <span className="pl-8 text-xs text-slate-400">
                  {feature.customer_name ?? "—"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Text type="secondary" className="text-xs">
        {totalCount !== null
          ? t("globalMap.quickView.nearby.recordsWithinRadius", {
              count: totalCount,
              miles: NEARBY_RADIUS_MI
            })
          : t("globalMap.quickView.nearby.radiusLabel", {
              miles: NEARBY_RADIUS_MI
            })}
      </Text>

      {counts && (
        <ul
          className="m-0 flex list-none flex-col divide-y divide-gray-100 rounded-lg border border-gray-100 p-0"
          data-testid="quick-view-nearby-breakdown"
        >
          {breakdownRows.map((row) => (
            <li
              key={row.key}
              className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
            >
              <span className="flex items-center gap-2 text-slate-700">
                <span
                  aria-hidden="true"
                  className="flex size-5 shrink-0 items-center justify-center text-base"
                  style={{ color: row.color }}
                >
                  {row.icon}
                </span>
                {row.label}
              </span>
              <span className="font-medium text-slate-900">
                {counts[row.key]}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export { QuickViewNearby }
